import React, { useMemo } from 'react';
import { S3Client, latestPackage, type PackageDetails, packageFactory } from '../../services/Clients/S3Client';
import ContextualBranchManager from '../../services/Dom/ContextualBranchManager';
import S3DialogSelector from '../S3DialogSelector/S3DialogSelector';
import ClickLabel from '../Ui/ClickLabel';
import LoadingIndicator from '../Ui/LoadingIndicator';
import { PackageTree } from '../../services/Clients/PackageTree';
import Dialog from '../Ui/Dialog';
import { useContextualS3Validation } from '../../hooks/useContextualS3Validation';

interface IProps {
  readonly label: string;
  readonly bucket: string;
  readonly subfolder?: string;
  readonly defaultSelection: string;
  readonly useDetailedView: boolean;
  readonly dialogIsOpened: boolean;
  readonly onDetails: () => void;
  readonly onArtifactSelect?: (artifactName: string, artifactVersion: string) => void;
}

const S3Selector: React.FC<IProps> = ({ label, bucket, subfolder = '', defaultSelection, dialogIsOpened, onDetails, onArtifactSelect }) => {
  const packageTreeInstance = useMemo(() => {
    return new PackageTree(new S3Client(bucket, subfolder));
  }, [bucket, subfolder]);

  const {
    currentBranch,
    isLoading,
    unvalidatedBranchName,
    unvalidatedVersionName,
    validationError,
    retry,
  } = useContextualS3Validation({
    label,
    defaultSelection,
    packageTreeInstance,
    onArtifactSelect,
  });

  const persistBranchSelection = (branch: PackageDetails, version: PackageDetails) => {
    ContextualBranchManager.setSelectedBranch(branch.folder, label);
    ContextualBranchManager.setSelectedVersion(version.folder, label);
  };

  const applyBranchSelectionChange = (branch: PackageDetails, version: PackageDetails) => {
    persistBranchSelection(branch, version);
    window.location.reload();
  };

  const onReset = () => {
    applyBranchSelectionChange(packageFactory(defaultSelection), latestPackage());
  };
  const onSubmit = () => {
    window.location.reload();
  };

  const branchLabel = currentBranch ? currentBranch.label : unvalidatedBranchName;
  const versionLabel = unvalidatedVersionName;

  if (isLoading) {
    return (
      <div className="env-switcher-label scolab-switcher-loading-label">
        <div>
          <label>{label}</label>
          <div className="scolab-switcher-loading-value">
            <LoadingIndicator label={`${label} loading`} />
          </div>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <ClickLabel
        label={label}
        title={validationError.message}
        value={<span>Retry</span>}
        onClick={retry}
      />
    );
  }

  return (
    <>
      <Dialog onCancel={onDetails} onSubmit={onSubmit} onReset={onReset} isOpened={dialogIsOpened}>
        <S3DialogSelector
          onSelectionChange={(data) => {
            if (data.branch && data.version) {
              persistBranchSelection(data.branch, data.version);
            }
          }}
          packageTree={packageTreeInstance}
          label={label}
          currentBranch={currentBranch?.folder ?? unvalidatedBranchName}
          currentVersion={unvalidatedVersionName}
        />
      </Dialog>
      <ClickLabel
        label={label}
        title={`${branchLabel} @${versionLabel} `}
        value={<span>{branchLabel} <i>@</i> {versionLabel}</span>}
        onClick={onDetails}
      />
    </>
  );
};

export default S3Selector;
